'use client';

import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from '@cobrastyle/ui';
import { claimFreeGift } from '@/actions/free-gift';
import { useCart } from '@/hooks';

/**
 * Claims a product's free gift after its qualifying product has landed in the
 * cart.
 *
 * Runs the mutation at most once per add — `add_free_gift_to_cart` has no
 * idempotency cap, so a duplicate call would add more gift units — and adopts the
 * refreshed cart the action returns instead of triggering another fetch.
 *
 * A failure is non-blocking by design: the qualifying product is already in the
 * cart, only the gift didn't make it, so the customer gets a toast and the add
 * still counts.
 */
export function useFreeGiftClaim() {
  const { setCart, cart } = useCart();
  const t = useTranslations('product');
  const inFlight = useRef(false);

  // Tracked in a ref, not captured in the callback: the claim runs from the
  // add-to-cart success edge, so the callback was created *before* the add and
  // its closure would still hold the pre-add cart. The ref is current by then,
  // because the engine has already committed the cart the add returned.
  const cartIdRef = useRef<string | undefined>(cart?.id);
  cartIdRef.current = cart?.id;

  const claim = useCallback(
    async (parentSku: string, quantity: number) => {
      if (inFlight.current) return;
      inFlight.current = true;

      try {
        const result = await claimFreeGift({ parentSku, quantity, cartId: cartIdRef.current });

        if (result.ok) {
          if (result.cart) setCart(result.cart);
        } else {
          toast.error(t(`freeGift.error.${result.reason}`));
        }
      } catch (error) {
        // The request never reached the backend (stale client bundle after a
        // deploy, dropped connection). Distinct from Magento refusing the gift:
        // nothing is known about the cart, so the customer is told to reload
        // rather than reassured. No auto-retry — the mutation isn't idempotent.
        console.error('[free-gift] claim request failed', error);
        toast.error(t('freeGift.error.request'));
      } finally {
        inFlight.current = false;
      }
    },
    [setCart, t],
  );

  return { claim };
}
